/**
 * The built-in CC0 model set (apps/web/public/world/models, credits in LICENSES.md and on the
 * /credits page). Sizes are target real-world meters; the bake normalises every model to them
 * (origin at the foot, front toward +Z where the model has a front).
 */

export const MODEL_BASE = "/world/models/";

export interface ModelDef {
  /** Path under MODEL_BASE. */
  file: string;
  /** Path of the simplified variant (optional). */
  lod1?: string;
  /** Target height in meters (the bake scales the model to it), or a length along Z. */
  height?: number;
  length?: number;
  /** Rotate the baked model about Y by this many quarter turns (when the source faces elsewhere). */
  quarterTurns?: number;
  /** Materials (by name) that take the per-instance tint. */
  tint?: readonly string[];
  /** Materials that light up at night: 1 headlights, 2 tail lights, 3 lamp. */
  glow?: Readonly<Record<string, 1 | 2 | 3>>;
  /** Colors replacing material colors by name. */
  palette?: Readonly<Record<string, string>>;
  /** Sway in the wind (trees). */
  wind?: boolean;
}

const nature = (name: string, lod1: boolean, extra: Partial<ModelDef>): ModelDef => ({
  file: `quaternius-nature/${name}.glb`,
  ...(lod1 ? { lod1: `quaternius-nature/${name}_lod1.glb` } : {}),
  wind: true,
  ...extra,
});

const LEAF = ["Green", "DarkGreen", "LightOrange", "Orange", "Berry"] as const;

/** Tree species (two variants of each; the autumn one is rare). */
export const TREES: readonly ModelDef[] = [
  nature("CommonTree_1", true, { height: 9, tint: LEAF }),
  nature("CommonTree_3", true, { height: 8, tint: LEAF }),
  nature("BirchTree_1", true, { height: 10, tint: ["Green", "DarkGreen"] }),
  nature("BirchTree_4", true, { height: 9, tint: ["Green", "DarkGreen"] }),
  nature("PineTree_1", true, { height: 11, tint: LEAF }),
  nature("PineTree_3", true, { height: 10, tint: LEAF }),
  nature("Willow_1", true, { height: 9, tint: LEAF }),
  nature("CommonTree_Autumn_2", true, { height: 9, tint: [] }),
];

export const BUSHES: readonly ModelDef[] = [
  nature("Bush_1", false, { height: 1.1, tint: LEAF }),
  nature("Bush_2", false, { height: 1.0, tint: LEAF }),
  nature("BushBerries_1", false, { height: 1.1, tint: ["Green"] }),
];

const CAR_BODY = ["White", "Blue", "LightBlue", "Orange", "DarkOrange", "Material.007", "Yellow"];
const car = (name: string, tint: readonly string[] = CAR_BODY): ModelDef => ({
  file: `quaternius-cars/${name}.glb`,
  lod1: `quaternius-cars/${name}_lod1.glb`,
  length: 4.5,
  tint,
  glow: { Headlights: 1, TailLights: 2 },
});

export const CARS: readonly ModelDef[] = [
  car("NormalCar1"),
  car("NormalCar2"),
  car("SportsCar"),
  car("SportsCar2"),
  car("SUV"),
  { ...car("Taxi", []), length: 4.6 },
  { ...car("Cop", []), length: 4.6 },
];

export const BUS: ModelDef = {
  file: "quaternius-transport/Bus.glb",
  lod1: "quaternius-transport/Bus_lod1.glb",
  length: 11,
  tint: [],
  glow: { Lights: 1 },
  palette: { Top: "#3d6fb5", Bottom: "#3d6fb5", Bumper: "#2c2f36", Details: "#d8dde3" },
};
export const SCHOOL_BUS: ModelDef = {
  file: "quaternius-transport/SchoolBus.glb",
  lod1: "quaternius-transport/SchoolBus_lod1.glb",
  length: 10,
  tint: [],
  glow: { Lights: 1 },
};
export const BICYCLE: ModelDef = {
  file: "quaternius-transport/Bicycle.glb",
  lod1: "quaternius-transport/Bicycle_lod1.glb",
  length: 1.8,
  tint: [],
};

export const LAMP: ModelDef = {
  file: "quaternius-streets/Streetlight_Single.glb",
  lod1: "quaternius-streets/Streetlight_Single_lod1.glb",
  height: 7,
  glow: { Light: 3 },
};
export const LAMP_DOUBLE: ModelDef = {
  file: "quaternius-streets/Streetlight_Double.glb",
  lod1: "quaternius-streets/Streetlight_Double_lod1.glb",
  height: 7,
  glow: { Light: 3 },
};
// The street pieces face +X in the source files: a quarter turn makes them face +Z.
export const TRAFFIC_LIGHT: ModelDef = {
  file: "quaternius-streets/TrafficLight.glb",
  height: 5.2,
  quarterTurns: -1,
};
export const SIGN_STOP: ModelDef = {
  file: "quaternius-streets/Sign_Stop.glb",
  height: 2.6,
  quarterTurns: -1,
};
export const SIGN_TRIANGLE: ModelDef = {
  file: "quaternius-streets/Sign_Triangle.glb",
  height: 2.6,
  quarterTurns: -1,
};
export const SIGN_NO_PARKING: ModelDef = {
  file: "quaternius-streets/Sign_NoParking.glb",
  height: 2.6,
  quarterTurns: -1,
};
export const BIN: ModelDef = { file: "quaternius-props/Trashcan_Large.glb", height: 1.0 };
export const CONE: ModelDef = { file: "quaternius-transport/TrafficCone.glb", height: 0.7 };

export interface CharacterDef {
  file: string;
  height: number;
}
export const CHARACTERS: readonly CharacterDef[] = [
  "Casual_2",
  "Casual_Hoodie",
  "Suit",
  "Worker",
  "Farmer",
  "Adventurer",
  "Beach",
].map((n) => ({ file: `quaternius-characters/${n}.glb`, height: 1.78 }));

export const modelUrl = (file: string): string => MODEL_BASE + file;
